import { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { API_BASE_URL } from '../api'

const PDF_NATIVE_WIDTH = 794
const PDF_NATIVE_HEIGHT = 1123

/**
 * Modal d'apercu PDF d'une facture (portail producteur).
 *
 * Telecharge le PDF en blob (avec credentials pour l'auth cookie) puis l'affiche
 * dans un iframe via createObjectURL — evite les problemes cross-origin /
 * cookies bloques que pose une iframe directe.
 */
function InvoicePreviewModal({ invoiceId, invoiceNumero, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const pdfContainerRef = useRef(null)
  const [pdfScale, setPdfScale] = useState(1)

  useLayoutEffect(() => {
    if (!blobUrl) return
    const compute = () => {
      const el = pdfContainerRef.current
      if (!el) return
      const w = el.clientWidth
      const h = el.clientHeight
      if (w === 0 || h === 0) return
      const scaleX = w / PDF_NATIVE_WIDTH
      const scaleY = h / PDF_NATIVE_HEIGHT
      setPdfScale(Math.min(scaleX, scaleY, 1))
    }
    compute()
    const observer = new ResizeObserver(compute)
    if (pdfContainerRef.current) observer.observe(pdfContainerRef.current)
    window.addEventListener('orientationchange', compute)
    return () => {
      observer.disconnect()
      window.removeEventListener('orientationchange', compute)
    }
  }, [blobUrl])

  useEffect(() => {
    let aborted = false
    let createdUrl = null

    const fetchPdf = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/pdf`, {
          credentials: 'include',
        })
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }
        const blob = await res.blob()
        if (aborted) return
        createdUrl = URL.createObjectURL(blob)
        setBlobUrl(createdUrl)
      } catch (err) {
        if (aborted) return
        console.error('Erreur preview PDF:', err)
        setError(err?.message || 'Impossible de charger le PDF')
      } finally {
        if (!aborted) setLoading(false)
      }
    }

    fetchPdf()

    return () => {
      aborted = true
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [invoiceId])

  const handleDownload = () => {
    if (!blobUrl) return
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = `facture-${invoiceNumero || invoiceId}.pdf`
    document.body.appendChild(a)
    a.click()
    setTimeout(() => document.body.removeChild(a), 100)
  }

  const handleOpenNewTab = () => {
    window.open(`${API_BASE_URL}/invoices/${invoiceId}/pdf`, '_blank')
  }

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3 sm:p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl flex flex-col shadow-2xl overflow-hidden" style={{ maxHeight: '90vh' }}>
        {/* Header */}
        <div className="px-3 py-2 sm:px-4 sm:py-3 border-b border-gray-100 flex items-center gap-2 bg-white">
          <h3 className="font-bold text-gray-800 flex-1 truncate text-sm sm:text-base">
            <span className="hidden sm:inline">Aperçu — </span>
            {invoiceNumero || 'Facture'}
          </h3>
          {blobUrl && !loading && (
            <button
              onClick={handleDownload}
              className="shrink-0 p-2 sm:px-3 sm:py-1.5 rounded-lg text-xs font-medium transition"
              style={{ background: 'rgb(var(--c-primary))', color: 'white' }}
              title="Télécharger"
              aria-label="Télécharger"
            >
              <span className="sm:hidden">⬇</span>
              <span className="hidden sm:inline">⬇ Télécharger</span>
            </button>
          )}
          <button
            onClick={onClose}
            className="shrink-0 w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full text-gray-600"
            aria-label="Fermer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Contenu — taille fixe + ResizeObserver pour scaler le PDF */}
        <div ref={pdfContainerRef} className="flex-1 bg-gray-100 overflow-hidden flex items-center justify-center p-2 sm:p-4">
          {loading && (
            <div className="text-center text-gray-500">
              <div className="w-8 h-8 border-3 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm">Chargement du PDF...</p>
            </div>
          )}
          {error && (
            <div className="text-center text-red-600 max-w-md p-6">
              <div className="text-3xl mb-2">⚠</div>
              <p className="text-sm font-medium mb-2">Impossible de charger le PDF</p>
              <p className="text-xs text-red-500 mb-4 break-words">{error}</p>
              <button
                onClick={handleOpenNewTab}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white"
                style={{ background: 'rgb(var(--c-primary))' }}
              >
                Ouvrir dans un nouvel onglet
              </button>
            </div>
          )}
          {blobUrl && !loading && !error && (
            <div
              style={{
                width: PDF_NATIVE_WIDTH * pdfScale,
                height: PDF_NATIVE_HEIGHT * pdfScale,
                position: 'relative',
              }}
              className="shadow-md bg-white"
            >
              <iframe
                src={blobUrl}
                title={`Facture ${invoiceNumero || ''}`}
                className="border-0 bg-white"
                style={{
                  width: PDF_NATIVE_WIDTH,
                  height: PDF_NATIVE_HEIGHT,
                  transform: `scale(${pdfScale})`,
                  transformOrigin: 'top left',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default InvoicePreviewModal
